import { ITableRow } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnConditionExpression,
  ColumnConditionValue,
  ConditionNumericOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  VariableResultDateTime,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'

import { parseVariableSelect } from '@/core/app-builder/parser/variable-select'
import { workerRequest } from '@/workers/worker-request'

import { IDBTable } from '../table'

const UNDEFINED_VALUE = '__UNDEFINED__'

export interface FilterConfig {
  settings: ColumnConditionValue
  userEmail: string
  tableId?: string
  detailRowIndex?: number
  detailRowId?: string
  detailTableId?: string
}

/**
 * Handle filtering of rows based on the filterSettings in app builder components.
 *
 * Used mainly in list component group
 */
export async function filterData(
  requestId: string,
  filter: FilterConfig | undefined,
  rows: ITableRow[],
  table: IDBTable,
  language: string,
  userSpecific: string
): Promise<ITableRow[]> {
  const { dataWorker } = table.dbClient

  // If there's no filter, no settings, or no conditions, return all rows immediately.
  if (!filter || !filter.settings || filter.settings.conditions.length === 0) {
    return Promise.resolve(rows)
  }

  // Pre-compute condition arguments for all conditions. This is efficient
  // as it's done only once, outside the main row-iteration loop.
  const { conditionArgs, isAny, tz } = composeFilterArgs(filter, table, language, userSpecific)

  try {
    const response = await workerRequest<{ rows: ITableRow[] }>(dataWorker, {
      type: 'table-data-filter',
      requestId: `${requestId}-${Date.now()}`,
      data: { isAny, rows, conditionArgs, tz },
    })

    return response.rows
    //
  } catch (err) {
    console.error('filterData error: ', err)
    return Promise.resolve(rows)
  }
}

// -------------------------------------------------------------------------------- //
//                                  Helper Functions                                //
// -------------------------------------------------------------------------------- //

function composeFilterArgs(filter: FilterConfig, table: IDBTable, language: string, userSpecific: string) {
  // get filter settings
  const {
    settings: { isAny = true, conditions = [] },
    userEmail,
    tableId,
    detailRowIndex,
    detailRowId,
    detailTableId,
  } = filter
  const params = { tableId, detailRowIndex, detailTableId, detailRowId }

  const conditionArgs = conditions.map((c) =>
    parseConditionArguments(c, table, userEmail, language, userSpecific, params)
  )

  const tz = table.settings._Tz || 'auto'

  return {
    isAny,
    conditionArgs,
    tz,
  }
}

interface FilterDataDetailsParams {
  tableId?: string
  /** @deprecated */
  detailRowIndex?: number
  detailRowId?: string
  detailTableId?: string
}

// Helper function to parse condition arguments once
function parseConditionArguments(
  cond: ColumnConditionExpression,
  table: IDBTable,
  userEmail: string,
  language: string,
  userSpecific: string,
  params: FilterDataDetailsParams
) {
  const lhsCol = table?.columnsMap.get(cond.lhs)
  const columnType = lhsCol?._Type

  // Parse RHS value once
  const rhsVal = parseRhsValue(cond, table, userEmail, language, userSpecific, params)
  const rhsVariant = cond.rhs ? (cond.rhs as VariableResultDateTime) : undefined

  return {
    columnId: cond.lhs,
    columnType,
    op: cond.operator,
    rhsVal,
    caseSensitive: cond.caseSensitive,
    rhsVariant: rhsVariant?.variant,
  }
}

// Helper to parse RHS value
function parseRhsValue(
  cond: ColumnConditionExpression,
  table: IDBTable,
  userEmail: string,
  language: string,
  userSpecific: string,
  params: FilterDataDetailsParams
) {
  if (!cond.rhs) return { value: UNDEFINED_VALUE }

  const dbClient = table.dbClient

  // When transitioning from range to single value,
  // we need to ensure rhsEnd is not included in the value
  const isRangeOperator =
    NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
    DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)

  let tableId: string | undefined

  // process variable flag to access detail table/row index
  switch (cond.rhs.flag) {
    case VariableSelectFeatureFlag.CurrentListItem:
    case VariableSelectFeatureFlag.CurrentRow:
    case VariableSelectFeatureFlag.Column:
      {
        tableId = params.detailTableId
      }
      break
    default:
      tableId = params.tableId
      break
  }

  if (isRangeOperator && cond.rhsEnd) {
    const [start] = parseVariableSelect(
      cond.rhs,
      dbClient,
      userEmail,
      language,
      userSpecific,
      tableId,
      params.detailRowId,
      false
    )
    const [end] = parseVariableSelect(
      cond.rhsEnd,
      dbClient,
      userEmail,
      language,
      userSpecific,
      tableId,
      params.detailRowId,
      false
    )
    return {
      start: start ?? UNDEFINED_VALUE,
      end: end ?? UNDEFINED_VALUE,
    }
  }

  // For non-range operators, only use rhs value
  const [value] = parseVariableSelect(
    cond.rhs,
    dbClient,
    userEmail,
    language,
    userSpecific,
    tableId,
    params.detailRowId,
    false
  )

  return { value: value ?? UNDEFINED_VALUE }
}
